<template>
  <div id="app">
    <div class="page__headlines">
      <h1
        class="pageHeadline pageHeadline--small"
        v-if="pageHeadline">
        {{ pageHeadline }}
      </h1>
      <div
        v-if="!list.onlySearch"
        class="pageHeadline__buttons clr no-print">
        <button
          class="button"
          v-if="helpText"
          @click="showHelp"
          :title="$t('list.help')">
          {{ $t('list.help') }}
        </button>
      </div>
    </div>
    <ExhibitorDirectory
      v-if="isList"
      :aside="list.aside"
      :limitedFairEvents="limitedFairEvents"
      :fairGroups="list.fairGroups"
      :fairGroupsTitle="fairGroupsTitle"
      :hallsTitle="hallsTitle"
      :categoriesTitle="categoriesTitle"
      :travelDestinationTitle="travelDestinationTitle"
      :onlySearch="list.onlySearch"
      :helpText="helpText"
      :hallplanPath="list.hallplanPath"
      :timeTablePath="list.timeTablePath"
      :woozlePath="list.woozlePath"
      :expandedCategories="expandedCategories"
      :expandCategoryDropdown="expandCategoryDropdown"
      :splittedCategories="splittedCategories"
      :hideResultAmount="hideResultAmount"
      :topOfTheListColor="topOfTheListColor"
      :terminalmode="terminalmode"
      :topOfTheListOpacity="topOfTheListOpacity"
      :listUrl="listUrl"/>

    <ExhibitorSearch
      v-if="isSearch"
      :positionX="search.positionX"
      :positionY="search.positionY"
      :color="search.color"
      :limitedFairEvents="limitedFairEvents"
      :teaser="search.teaser"
      :listUrl="listUrl"/>

    <template v-if="fairEventUids.length > 0 && loginUrl && isLoggedIn()">
      <tracking-modal
        :login-url="loginUrl"
        :fair-event-uids="fairEventUids"
        @allow="attemptTrack"
        @object="()=>{}"
      />
    </template>

    <transition name="modal">
      <div
        class="modal-mask"
        id="help-modal"
        v-if="detail.isHelpVisible">
        <div class="modal-wrapper">
          <div
            class="modal-container modal-container--small">
            <div class="modal__buttons clr">
              <button
                class="modal-close"
                @click="closeHelp"
                :title="$t('detail.closeTooltip')">
                <i class="icon icon-close"></i>
              </button>
            </div>

            <p class="h2">{{ $t('list.help') }}</p>

            <div v-html="formatHelpText"></div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'; // eslint-disable-line
import { TrackingUtility } from './common/TrackingUtility';
import { MkdbService } from './common/mkdb.service';
import ExhibitorDirectory from './components/ExhibitorDirectory/ExhibitorDirectory.vue';
import ExhibitorSearch from './components/ExhibitorSearch/ExhibitorSearch.vue';
import TrackingModal from './components/TrackingModal/TrackingModal.vue';
import StorageService from '@/common/storage.service';

export default {
  name: 'App',
  components: {
    ExhibitorDirectory,
    ExhibitorSearch,
    TrackingModal,
  },
  data() {
    return {
      pageHeadline: '',
      helpText: '',
      type: '',
      listUrl: '',
      fairGroupsTitle: '',
      hallsTitle: '',
      categoriesTitle: '',
      travelDestinationTitle: '',
      limitedFairEvents: [],
      expandedCategories: false,
      expandCategoryDropdown: false,
      splittedCategories: false,
      hideResultAmount: false,
      topOfTheListColor: '',
      terminalmode: false,
      topOfTheListOpacity: 0.8,
      list: {
        aside: false,
        onlySearch: false,
        fairGroups: [],
        hallplanPath: '',
        timeTablePath: '',
        woozlePath: '',
      },
      detail: {
        isHelpVisible: false,
      },
      search: {
        positionX: '',
        positionY: '',
        color: '',
        teaser: false,
      },
      trackingUtilities: [],
      lmsUser: null,
      fairEventUids: [],
      loginUrl: null,
      mkdbUrl: null,
    };
  },
  async created() {
    const settingsNode = document.getElementById('ed-settings');

    if (this.isLoggedIn()) {
      this.initTracking(settingsNode);
    }

    this.type = settingsNode.dataset.type;
    this.listUrl = settingsNode.dataset.listurl;
    this.helpText = settingsNode.dataset.help === '""' ? '' : settingsNode.dataset.help;
    this.pageHeadline = settingsNode.dataset.headline;
    this.fairGroupsTitle = settingsNode.dataset.fairgroupstitle;
    this.hallsTitle = settingsNode.dataset.hallstitle;
    this.categoriesTitle = settingsNode.dataset.categoriestitle;
    this.travelDestinationTitle = settingsNode.dataset.traveldestinationtitle;
    this.limitedFairEvents = settingsNode.dataset.limitedfairevents ? settingsNode.dataset.limitedfairevents : '[]';
    this.expandedCategories = settingsNode.dataset.expandedcategories === '1';
    this.expandCategoryDropdown = settingsNode.dataset.expandcategorydropdown === '1';
    this.splittedCategories = settingsNode.dataset.splittedcategories === '1';
    this.hideResultAmount = settingsNode.dataset.hideresultamount === '1';
    this.topOfTheListColor = settingsNode.dataset.topofthelistcolor;
    this.terminalmode = settingsNode.dataset.terminalmode === '1';
    try {
      this.topOfTheListOpacity = parseFloat(settingsNode.dataset.topofthelistopacity);
    } catch (error) {
      /* set to default */
      this.topOfTheListOpacity = 0.8;
    }
    if (this.type === 'list') {
      this.list.hallplanPath = settingsNode.dataset.hallplanpath;
      this.list.woozlePath = settingsNode.dataset.woozlepath;
      this.list.timeTablePath = settingsNode.dataset.timetablepath;
      this.list.aside = settingsNode.dataset.aside === 'true';
      this.list.onlySearch = settingsNode.dataset.onlysearch === 'true';
      this.list.fairGroups = settingsNode.dataset.fairgroups ? settingsNode.dataset.fairgroups : '[]';
    } else {
      this.search.positionX = settingsNode.dataset.positionx;
      this.search.positionY = settingsNode.dataset.positiony;
      this.search.color = settingsNode.dataset.color;
      this.search.teaser = settingsNode.dataset.teaser === 'true';
    }
  },
  methods: {
    showHelp() {
      this.detail.isHelpVisible = true;
      const targetElement = document.querySelector('#help-modal');
      disableBodyScroll(targetElement);
    },
    closeHelp() {
      this.detail.isHelpVisible = false;
      clearAllBodyScrollLocks();
    },
    isLoggedIn() {
      const lmsUserData = StorageService.getItem('buyerData');
      if (lmsUserData) {
        try {
          const lmsUser = JSON.parse(lmsUserData);
          return Boolean(lmsUser) && Boolean(lmsUser.accountCode);
        } catch (e) {
          console.error(e);
        }
      }
      return false;
    },
    async initTracking(settingsNode) {
      this.loginUrl = settingsNode.dataset.loginUrl;
      this.mkdbUrl = settingsNode.dataset.mkdbUrl;
      const fairUids = JSON.parse(settingsNode.dataset.limitedfairevents ? settingsNode.dataset.limitedfairevents : '[]');
      const mkdbService = new MkdbService(this.mkdbUrl);
      const requests = [];
      fairUids.forEach(f => requests.push(mkdbService.getFair(f)));
      const fairs = await Promise.all(requests);
      this.fairEventUids = fairs.filter(f => f).map(f => f.fairEventUid);
      this.fairEventUids.forEach(fuid =>
        this.trackingUtilities.push(new TrackingUtility(
          {
            siteId: settingsNode.dataset.trackingSiteId,
            trackingDomain: settingsNode.dataset.trackingDomain,
            enableHeartBeatTimer: true,
            enableLinkTracking: true,
            trackPageView: true,
          },
          fuid,
        )),
      );
      this.attemptTrack();
    },
    async attemptTrack() {
      if (this.trackingUtilities.length > 0) {
        this.trackingUtilities[0].clearStorage();
        this.lmsUser = this.trackingUtilities[0].getLmsUser();
        if (this.lmsUser && this.lmsUser.accountCode) {
          this.trackingUtilities.forEach(
            tu => {
              tu.setUserId(this.lmsUser.accountCode);
              tu.init();
              if (this.$route.params.exhibitorId) {
                const exhibitorId = Number.parseInt(`${ this.$route.params.exhibitorId }`, 10);
                if (!Number.isNaN(exhibitorId)) {
                  tu.setExhibitorIds(this.lmsUser.accountCode, [exhibitorId]);
                }
              }
            },
          );
        } else {
          this.trackingUtilities[0].clearStorage();
        }
      }
    },
  },
  computed: {
    isList() {
      return this.type === 'list';
    },
    isSearch() {
      return this.type === 'search';
    },
    formatHelpText() {
      return JSON.parse(this.helpText);
    },
  },
  watch: {
    '$route.params': {
      deep: true,
      handler(newParams, oldParams) {
        // TODO: track exhibitor only if part of accepted fair event
        if (
          newParams.exhibitorId && !oldParams.exhibitorId
          || newParams.exhibitorId && oldParams.exhibitorId && newParams.exhibitorId !== oldParams.exhibitorId
        ) {
          const exhibitorId = Number.parseInt(`${ newParams.exhibitorId }`, 10);
          if (!Number.isNaN(exhibitorId)) {
            this.trackingUtilities.forEach((tu) => {
              tu.setExhibitorIds(this.lmsUser.accountCode, [exhibitorId]);
            });
          }
        }
      },
    },
  },
};
</script>
